"use client";

import type { MissionData, Opportunity } from "@/types/mission";

/**
 * Print-only truthful mission report (Ctrl/Cmd+P, the "p" shortcut, or the
 * command palette's "Print mission report"). Hidden on screen; rendered as
 * the entire page content in print via the print stylesheet.
 *
 * Everything is derived 1:1 from live mission/*.json state — no synthesis,
 * no fabrication. Verified earnings of $0.00 is the honest truth until an
 * on-chain txid says otherwise. 402 handshakes are explicitly NOT earnings.
 *
 * Static markup only (no framer-motion): print happens on demand and this
 * tree is display:none on screen, so animation would be wasted work.
 */

function isViable(o: Opportunity) {
  return o.status === "validated" || o.status === "validated-with-blockers" || o.status === "active";
}
function isRejected(o: Opportunity) {
  return o.status === "rejected" || o.status === "dead";
}

const PRIORITY_ORDER: Record<string, number> = { P0: 0, P1: 1, P2: 2, P3: 3 };

function dayCount(data: MissionData): number {
  const start = new Date(data.state.started_at).getTime();
  const end = new Date(data.generated_at).getTime();
  if (Number.isNaN(start) || Number.isNaN(end)) return 1;
  return Math.max(1, Math.floor((end - start) / 86_400_000) + 1);
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="mt-5 break-inside-avoid">
      <h2 className="border-b border-zinc-400 pb-1 font-mono text-[11px] font-bold uppercase tracking-[0.14em] text-zinc-800">
        {title}
      </h2>
      <div className="mt-2">{children}</div>
    </section>
  );
}

export function PrintReport({ data }: { data: MissionData }) {
  const s = data.state;
  const verified = data.earnings.filter((e) => e.status === "verified");
  const usd = s.verified_earnings_total.usd_estimate;
  const viable = data.pipeline.filter(isViable).length;
  const rejected = data.pipeline.filter(isRejected).length;
  const other = data.pipeline.length - viable - rejected;
  const day = dayCount(data);
  const f = data.flagship;
  const ladderAchieved = f ? f.ladder.filter((r) => r.achieved).length : 0;

  const queue = [...data.next_actions]
    .filter((n) => n.status !== "done" && n.status !== "cancelled")
    .sort((a, b) => (PRIORITY_ORDER[a.priority] ?? 9) - (PRIORITY_ORDER[b.priority] ?? 9))
    .slice(0, 6);

  return (
    <div className="hidden max-w-full text-zinc-900 print:block">
      {/* masthead */}
      <header className="break-inside-avoid">
        <h1 className="font-mono text-lg font-bold tracking-[0.14em]">{s.mission} — Mission Report</h1>
        <p className="mt-1 font-mono text-[10px] leading-relaxed text-zinc-600">
          generated {data.generated_at} · phase {s.phase} · mission day {day} · {s.session_count} autonomous
          sessions · objective: {s.objective}
        </p>
      </header>

      {/* honest bottom line */}
      <Section title="Honest bottom line">
        <table className="w-full text-[11px] leading-relaxed">
          <tbody>
            <tr>
              <td className="w-40 align-top font-semibold">Verified earnings</td>
              <td className="align-top">
                ${usd.toFixed(2)} ({verified.length} verified entries) — on-chain evidence only; a 402 payment
                handshake is NOT an earning.
              </td>
            </tr>
            <tr>
              <td className="align-top font-semibold">Pipeline</td>
              <td className="align-top">
                {data.pipeline.length} opportunities tracked · {viable} viable · {rejected} rejected · {other} other.
              </td>
            </tr>
            <tr>
              <td className="align-top font-semibold">Evidence trail</td>
              <td className="align-top">
                {data.actions.length} logged actions · {data.decisions.length} decisions · {data.research.length}{" "}
                research notes.
              </td>
            </tr>
          </tbody>
        </table>
      </Section>

      {/* flagship */}
      {f && (
        <Section title={`Flagship — ${f.service}`}>
          <table className="w-full text-[11px] leading-relaxed">
            <tbody>
              <tr>
                <td className="w-40 align-top font-semibold">Service</td>
                <td className="align-top">{f.description}</td>
              </tr>
              <tr>
                <td className="align-top font-semibold">Endpoint</td>
                <td className="break-all align-top font-mono text-[10px]">
                  {f.public_base} · network {f.network}
                </td>
              </tr>
              {f.prices.map((p) => (
                <tr key={p.route}>
                  <td className="align-top font-semibold">Price</td>
                  <td className="align-top font-mono text-[10px]">
                    {p.price} — {p.route} ({p.note})
                  </td>
                </tr>
              ))}
              <tr>
                <td className="align-top font-semibold">Probes @ {f.checked_at}</td>
                <td className="align-top">
                  root HTTP {f.probes.root_status} · wallet-balance 402: {f.probes.wallet_balance_402 ? "yes" : "no"}{" "}
                  · research-brief 402: {f.probes.research_brief_402 ? "yes" : "no"}
                </td>
              </tr>
            </tbody>
          </table>
          <p className="mt-2 text-[11px] font-semibold">
            Six-state ladder: {ladderAchieved}/{f.ladder.length} achieved
          </p>
          <ul className="mt-1 space-y-0.5 text-[11px] leading-relaxed">
            {f.ladder.map((r) => (
              <li key={r.key} className="flex gap-2">
                <span className="w-3 shrink-0 font-mono">{r.achieved ? "✓" : "…"}</span>
                <span>
                  <span className="font-semibold">{r.label}</span> — {r.note}
                </span>
              </li>
            ))}
          </ul>
          {!f.buyer_funding.funded_for_settlement_test ? (
            <p className="mt-2 text-[11px] leading-relaxed">
              <span className="font-bold">SINGLE UNLOCK (30s owner action):</span> claim 20 test-USDC (Base Sepolia)
              at faucet.circle.com to buyer <span className="break-all font-mono text-[10px]">{f.buyer_funding.address}</span>{" "}
              → settlement test → env-only mainnet flip. Current buyer funding:{" "}
              {f.buyer_funding.base_sepolia_usdc ?? 0} USDC / {f.buyer_funding.base_sepolia_eth ?? 0} ETH.
            </p>
          ) : (
            <p className="mt-2 text-[11px] leading-relaxed">
              Buyer funded ({f.buyer_funding.base_sepolia_usdc ?? 0} test-USDC) — settlement test ready.
            </p>
          )}
          <p className="mt-1 text-[11px] leading-relaxed">Mainnet flip plan: {f.mainnet_flip}</p>
        </Section>
      )}

      {/* radars */}
      <Section title="Radars (latest scans)">
        <ul className="space-y-1 text-[11px] leading-relaxed">
          {data.ste_radar && (
            <li>
              Superteam Earn agent listings: {data.ste_radar.open_listings.length} open ·{" "}
              {data.ste_radar.new_since_last.length} new since last scan (checked {data.ste_radar.checked_at}).
            </li>
          )}
          {data.dealwork_radar && (
            <li>
              Dework bidding pool: {data.dealwork_radar.bidding_pool_total} scanned ·{" "}
              {data.dealwork_radar.real_tasks.length} real tasks · {data.dealwork_radar.ad_posts} ad-posts ·{" "}
              {data.dealwork_radar.listing_pending_requests.length} pending listing requests (checked{" "}
              {data.dealwork_radar.checked_at}).
            </li>
          )}
          {!data.ste_radar && !data.dealwork_radar && <li>(no radar scans recorded yet)</li>}
        </ul>
      </Section>

      {/* next actions */}
      <Section title="Next actions (highest priority first)">
        <ol className="space-y-1.5 text-[11px] leading-relaxed">
          {queue.length === 0 && <li>(queue empty)</li>}
          {queue.map((n, i) => (
            <li key={n.id} className="break-inside-avoid">
              <span className="font-semibold">
                {i + 1}. [{n.priority}] {n.id} — {n.title}
              </span>{" "}
              <span className="text-zinc-600">
                ({n.status}
                {n.blocked_by ? `, blocked by: ${n.blocked_by}` : ""})
              </span>
              <br />
              <span className="text-zinc-700">{n.rationale}</span>
            </li>
          ))}
        </ol>
      </Section>

      {/* wallets */}
      <Section title="Receiving wallets">
        <table className="w-full text-[11px] leading-relaxed">
          <tbody>
            {(data.monitoring?.results ?? []).map((r) => (
              <tr key={r.chain}>
                <td className="w-40 align-top font-semibold">{r.chain}</td>
                <td className="break-all align-top font-mono text-[10px]">
                  {r.address} — {r.ok ? `balance ${r.balance ?? "0"} (checked ${data.monitoring!.checked_at})` : "check failed"}
                </td>
              </tr>
            ))}
            {!data.monitoring &&
              Object.entries(s.wallets).map(([chain, addr]) => (
                <tr key={chain}>
                  <td className="w-40 align-top font-semibold">{chain}</td>
                  <td className="break-all align-top font-mono text-[10px]">{addr}</td>
                </tr>
              ))}
          </tbody>
        </table>
        {verified.length === 0 && (
          <p className="mt-1 text-[11px] text-zinc-700">
            All watched balances remain at zero since the 2026-09-11 baseline — honest.
          </p>
        )}
      </Section>

      {/* footer */}
      <p className="mt-6 border-t border-zinc-400 pt-2 font-mono text-[9px] leading-relaxed text-zinc-600">
        Honest-by-design: this report renders only verified mission state from mission/*.json. An earning counts
        only with on-chain/platform evidence (txid or link). Verified total so far: ${usd.toFixed(2)}.
      </p>
    </div>
  );
}
